@let _fullscreen = fullscreen();
@let _isMaximized = isMaximized();
@let _isMinimized = isMinimized();
@let _autoCenter = autoCenter();
@let _isOverlayVisibile = isOverlayVisible();
@let _icon = icon();
@let _title = title();
@let _titleVisible = titleVisible();
@let _error = error();
@let _isDialog = dialog();
@let _contentOverflowHidden = contentOverflowHidden();
@let _backdropBlur = backdropBlur();

@switch (PLATFORM) {
  @case (PlatformType.MOBILE) {
    <div
      #container
      class="flex flex-1 flex-col bg-white"
      [attr.role]="_isDialog ? 'dialog' : null"
      [attr.aria-modal]="_isDialog ? 'true' : null"
      [attr.aria-labelledby]="_isDialog && _titleVisible ? dialogTitleId : null"
    >
      <!-- header -->
      <div
        class="flex flex-row px-2 pt-2 items-center"
        [ngClass]="{ 'justify-end': !_titleVisible }"
      >
        @if (_titleVisible) {
          <!-- title -->
          <div class="flex flex-row items-center flex-1 gap-2">
            <div class="relative flex items-center justify-center w-6 h-6">
              <i class="{{ _icon }} text-xl text-slate-900"></i>
            </div>

            <h2
              [id]="_isDialog ? dialogTitleId : null"
              class="m-0 text-base font-normal text-slate-900"
            >
              @if (dirty$ && (dirty$ | async)) {
                <span class="font-medium text-pink-600">*</span>
              }

              {{ _title }}
            </h2>
          </div>
        }

        <!-- buttons -->
        <div class="flex flex-row items-center gap-1">
          @if (!hideButtonClose()) {
            <snt-button-icon
              [disableHover]="true"
              icon="fa-light fa-xmark"
              (onClick)="onClose()"
            ></snt-button-icon>
          }
        </div>
      </div>

      <!-- content -->
      <div class="flex-1 flex overflow-hidden p-2">
        <div class="relative flex flex-1">
          <div
            #componentContainer
            class="absolute inset-0"
            [ngClass]="{
              'overflow-hidden': _contentOverflowHidden,
              'overflow-auto': !_contentOverflowHidden
            }"
          ></div>
        </div>
      </div>

      <!-- footer -->
      @if (_error || (buttons()?.length || 0) > 0) {
        <div class="flex flex-col justify-end gap-2 p-2">
          <div class="flex flex-row flex-1">
            @if (_error) {
              <div class="flex-1 break-all">
                <span class="text-sm text-red-500">{{ _error }}</span>
              </div>
            }
          </div>

          <div class="flex flex-row">
            <div class="flex-1 flex flex-row items-center justify-start">
              @for (button of secondaryButtons(); track $index) {
                <snt-button
                  [disabled]="!!button.disabled"
                  [icon]="button.icon"
                  [title]="button.title"
                  [type]="button.type"
                  (onClickAsync)="executeButtonClick($event, button)"
                ></snt-button>
              }

              @if (isCheckboxVisible()) {
                <dx-check-box
                  [text]="checkboxLabel() || ''"
                  [iconSize]="16"
                  (onValueChanged)="onCheckboxValueChanged($event)"
                ></dx-check-box>
              }
            </div>

            <div class="flex-1"></div>

            <div class="flex flex-row justify-end gap-2">
              @for (button of buttons(); track $index) {
                <snt-button
                  [disabled]="!!button.disabled"
                  [icon]="button.icon"
                  [title]="button.title"
                  [type]="button.type"
                  (onClickAsync)="executeButtonClick($event, button)"
                ></snt-button>
              }
            </div>
          </div>
        </div>
      }
    </div>
  }
  @case (PlatformType.DESKTOP) {
    <!-- overlay -->
    <div
      [ngClass]="{
        'flex-1 flex': _isMaximized,
        'bg-slate-900/25':
          (_isMaximized && !_fullscreen && _isOverlayVisibile) || _fullscreen,
        'backdrop-blur-sm':
          _backdropBlur &&
          ((_isMaximized && !_fullscreen && _isOverlayVisibile) || _fullscreen),
        'items-center justify-center': _isMaximized && _autoCenter
      }"
      class="overflow-visible relative"
      (mousedown)="onOverlayClick()"
    >
      <!-- container -->
      <div
        #container
        [attr.role]="_isDialog && _isMaximized ? 'dialog' : null"
        [attr.aria-modal]="_isDialog && _isMaximized ? 'true' : null"
        [attr.aria-labelledby]="
          _isDialog && _isMaximized && _titleVisible ? dialogTitleId : null
        "
        [ngClass]="{
          'gap-2 bg-white': _isMaximized,
          'divide-y divide-slate-100': _isMaximized && _titleVisible,
          'bg-blue-500 border-t-2 border-x-2 border-b-0 rounded-t-md py-1 px-2 transition-all select-none cursor-pointer':
            _isMinimized,
          'min-w-[384px]': _isMaximized && !_fullscreen,
          'rounded-md': _isMaximized && !_fullscreen,
          'w-full h-full': _fullscreen,
          'grid grid-rows-[auto_minmax(0,1fr)_auto] max-h-[95vh] overflow-hidden':
            _isMaximized && !_fullscreen,
          'flex flex-col': _fullscreen || _isMinimized,
          absolute: !_autoCenter,
          'shadow-lg': !_isOverlayVisibile
        }"
        [ngStyle]="{
          'left.px': x(),
          'top.px': y(),
          height: _fullscreen
            ? null
            : _isMaximized
              ? contentHeight()
                ? contentHeight()
                : '384px'
              : '0'
        }"
        class="pointer-events-auto"
        (mousedown)="onContainerClick($event)"
      >
        <!-- header -->
        <div
          [ngClass]="{
            'pt-2 shrink-0': _isMaximized,
            'gap-4': _isMinimized,
            'px-4': _isMaximized && _fullscreen,
            'px-2': _isMaximized && !_fullscreen,
            'justify-end': !_titleVisible
          }"
          class="flex flex-row"
        >
          @if (_titleVisible) {
            <!-- title -->
            <div
              [ngClass]="{
                'text-xl gap-2': !_fullscreen,
                'text-3xl font-light gap-4': _fullscreen
              }"
              class="flex-1 flex flex-row items-center"
            >
              <div
                [ngClass]="{
                  'w-6 h-6': !_fullscreen
                }"
                class="relative flex items-center justify-center"
              >
                <i
                  [ngClass]="{
                    'text-slate-900': _isMaximized,
                    'text-slate-50': _isMinimized
                  }"
                  class="{{ _icon }}"
                ></i>
              </div>

              <div>
                <h2
                  [id]="_isDialog && _isMaximized ? dialogTitleId : null"
                  [ngClass]="{
                    'text-slate-900': _isMaximized,
                    'text-slate-50': _isMinimized,
                    'text-xl font-normal': !_fullscreen,
                    'text-3xl font-light': _fullscreen
                  }"
                  class="m-0"
                >
                  @if (dirty$ && (dirty$ | async)) {
                    <span class="font-medium text-pink-600">*</span>
                  }

                  {{ _title }}
                </h2>
              </div>
            </div>
          } @else {
            <div class="flex-1"></div>
          }

          <!-- buttons -->
          <div class="flex flex-row items-center gap-1">
            @if (_isMaximized) {
              @if (!dialog() && _autoCenter) {
                <snt-button-icon
                  icon="fa-light fa-window-minimize"
                  [disableHover]="true"
                  (onClick)="status.set('minimized')"
                ></snt-button-icon>
              }

              @if (!hideButtonClose()) {
                <snt-button-icon
                  [disableHover]="true"
                  [size]="_fullscreen ? '3xl' : 'base'"
                  icon="fa-light fa-xmark"
                  (onClick)="onClose()"
                ></snt-button-icon>
              }
            }

            @if (_isMinimized) {
              <snt-button-icon
                icon="fa-light fa-up-right-and-down-left-from-center"
                size="xs"
                [disableHover]="true"
                textColor="text-slate-50"
                (onClick)="status.set('maximized')"
              ></snt-button-icon>
            }
          </div>
        </div>

        <!-- content -->
        <div
          class="flex overflow-hidden min-h-0 transition-[min-height]"
          [ngClass]="{ 'p-2': _isMaximized, 'flex-1': _fullscreen }"
          [ngStyle]="{
            width: _isMaximized ? (contentWidth() ? contentWidth() : null) : '0'
          }"
        >
          <div class="relative flex flex-1">
            <div
              #componentContainer
              class="absolute inset-0"
              [ngClass]="{
                'overflow-hidden': _contentOverflowHidden,
                'overflow-auto': !_contentOverflowHidden
              }"
            ></div>
          </div>
        </div>

        <!-- footer -->
        @if (_isMaximized && (error() || (buttons()?.length || 0) > 0)) {
          <div
            class="flex shrink-0 flex-col justify-end gap-2"
            [ngClass]="{ 'px-2 py-2': _isMaximized }"
          >
            <div class="flex flex-row flex-1">
              @if (_error) {
                <div class="flex-1 break-all">
                  <span class="text-sm text-red-500">{{ _error }}</span>
                </div>
              }
            </div>

            <div class="flex flex-row">
              <div class="flex-1 flex flex-row items-center justify-start">
                @for (button of secondaryButtons(); track $index) {
                  <snt-button
                    [disabled]="!!button.disabled"
                    [icon]="button.icon"
                    [title]="button.title"
                    [type]="button.type"
                    (onClickAsync)="executeButtonClick($event, button)"
                  ></snt-button>
                }

                @if (isCheckboxVisible()) {
                  <dx-check-box
                    [text]="checkboxLabel() || ''"
                    [iconSize]="16"
                    (onValueChanged)="onCheckboxValueChanged($event)"
                  ></dx-check-box>
                }
              </div>

              <div class="flex-1"></div>

              <div class="flex flex-row justify-end gap-2">
                @for (button of buttons(); track $index) {
                  <snt-button
                    [disabled]="!!button.disabled"
                    [icon]="button.icon"
                    [title]="button.title"
                    [type]="button.type"
                    (onClickAsync)="executeButtonClick($event, button)"
                  ></snt-button>
                }
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  }
}
